import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import CurrencySelector from './CurrencySelector';
import CurrencySelectorMobile from './mobile/CurrencySelectorMobile';
import NotificationBell from './NotificationBell';
import NotificationPanelMobile from './mobile/NotificationPanelMobile';
import HighlightedAvatar from './HighlightedAvatar';
import { useNotifications } from '../contexts/NotificationContext';
import AnimatedPostButton from './AnimatedPostButton';
import { FiBell, FiMessageSquare, FiPlusSquare, FiUser, FiLogOut } from 'react-icons/fi';
import '../styles/Header.css';
import '../styles/CurrencySelectorMobile.css';
import '../styles/Notifications.css';
import '../styles/Chat.css';

const Header = ({ onSearch, searchQuery, setSearchQuery, isSearchEnabled = true, onReset }) => {
    const { isAuthenticated, logout, user } = useContext(AuthContext);
    const navigate = useNavigate();
    const canSearch = !!isSearchEnabled;
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [showMobileNotifications, setShowMobileNotifications] = useState(false);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const location = useLocation();
    const isCompanionsPage = location.pathname === '/companions' || location.pathname.startsWith('/companions/');

    // Get unread count for mobile badge (only when authenticated)
    let unreadCount = 0;
    try {
        const notificationContext = useNotifications();
        unreadCount = notificationContext?.unreadCount || 0;
    } catch {
        // Context not available, keep unreadCount at 0
    }

    const handleLogout = () => {
        logout();
        navigate('/');
        setShowMobileMenu(false);
    };

    const handleMobileMenuToggle = () => {
        setShowMobileMenu(!showMobileMenu);
    };

    const handleLogoClick = () => {
        if (onReset) {
            onReset();
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (!canSearch) return;
        if (onSearch && searchQuery.trim()) {
            onSearch(searchQuery);
        }
    };

    // Close profile dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showProfileDropdown && !event.target.closest('.profile-dropdown-container')) {
                setShowProfileDropdown(false);
            }
        };

        if (showProfileDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showProfileDropdown]);

    return (
        <>
            <header className="site-header" role="banner">
                <div className="site-header__inner">
                    <Link className="logo" to="/" aria-label="На главную" onClick={handleLogoClick}>
                        <img src="/wism_logo.png" alt="Хочувлето" />
                    </Link>

                    <form className="search" role="search" aria-label="Поиск по сайту" onSubmit={handleSearch}>
                        <input 
                            className="search__input" 
                            type="search" 
                            placeholder="ВВЕДИТЕ КЛЮЧЕВОЕ СЛОВО..." 
                            value={searchQuery || ''}
                            onChange={(e) => {
                                if (!canSearch) return;
                                setSearchQuery && setSearchQuery(e.target.value)
                            }}
                            disabled={!canSearch}
                        />
                        <button className="search__btn" type="submit" aria-label="Найти" disabled={!canSearch}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                 strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="7"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                        </button>
                    </form>

                    <div className="auth-buttons">
                        <CurrencySelector />
                        {!isAuthenticated ? (
                            <>
                                <Link to="/login" className="btn btn--ghost">ВХОД</Link>
                                <Link to="/register" className="btn btn--accent">РЕГИСТРАЦИЯ</Link>
                                <AnimatedPostButton
                                    onClick={() => navigate(isCompanionsPage ? '/companions/create' : '/post-create')}
                                    variant="desktop"
                                    text={isCompanionsPage ? 'Позвать' : 'Поделиться поездкой'}
                                    theme={isCompanionsPage ? 'trip' : 'post'}
                                />
                            </>
                        ) : (
                            <>
                                {/* Notification Bell */}
                                <NotificationBell />
                                
                                {/* Profile Picture Button with Dropdown - Facebook style */}
                                <div className="profile-dropdown-container">
                                    <button 
                                        className="profile-picture-btn" 
                                        aria-label="Профиль"
                                        onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                                        aria-expanded={showProfileDropdown}
                                    >
                                        <div className="profile-picture-container">
                                            <HighlightedAvatar
                                                src={user?.profile_image}
                                                alt={user?.username || 'Profile'}
                                                username={user?.username}
                                                userId={user?.id}
                                                isHighlighted={user?.is_highlighted || false}
                                                isPrivate={user?.is_private || false}
                                                className="profile-picture-img"
                                                imageProps={{
                                                    placeholder: "blur",
                                                    blurhash: user?.profile_image_blurhash,
                                                    priority: true,
                                                    lazy: false
                                                }}
                                            />
                                        </div>
                                        <svg 
                                            className="profile-picture-chevron" 
                                            viewBox="0 0 20 20" 
                                            fill="currentColor"
                                            aria-hidden="true"
                                        >
                                            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                                        </svg>
                                    </button>
                                    
                                    {/* Profile Dropdown Menu */}
                                    {showProfileDropdown && (
                                        <div className="profile-dropdown-menu">
                                            <Link 
                                                to="/profile" 
                                                className="profile-dropdown-item"
                                                onClick={() => setShowProfileDropdown(false)}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                                     strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="12" cy="7" r="4"></circle>
                                                </svg>
                                                <span>Профиль</span>
                                            </Link>
                                            <Link 
                                                to="/chat" 
                                                className="profile-dropdown-item"
                                                onClick={() => setShowProfileDropdown(false)}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                                     strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                                </svg>
                                                <span>Сообщения</span>
                                            </Link>
                                        </div>
                                    )}
                                </div>
                                
                                <Link to="/post-create" className="btn btn--accent">НОВАЯ ЗАПИСЬ</Link>
                                <button onClick={handleLogout} className="btn btn--ghost">ВЫХОД</button>
                            </>
                        )}
                    </div>

                    {/* Mobile burger menu button - visible for everyone */}
                    <button 
                        className="mobile-menu-btn" 
                        onClick={handleMobileMenuToggle}
                        aria-label="Открыть меню"
                    >
                        <span className="burger-line"></span>
                        <span className="burger-line"></span>
                        <span className="burger-line"></span>
                    </button>
                </div>
            </header>

            {/* Mobile menu modal - visible for everyone */}
            {showMobileMenu && (
                <div className="mobile-menu-modal" role="dialog" aria-modal="true" aria-label="Меню навигации">
                    <div className="mobile-menu-backdrop" onClick={() => setShowMobileMenu(false)} />
                    <div className="mobile-menu-panel">
                        <button 
                            className="mobile-menu-close" 
                            aria-label="Закрыть меню" 
                            onClick={() => setShowMobileMenu(false)}
                        >
                            ×
                        </button>
                        <div className="mobile-menu-panel-content">
                            <nav className="mobile-menu-nav">
                                {/* Auth menu items - only for authenticated users */}
                                {isAuthenticated && (
                                    <div className="mobile-menu-groups">

                                        {/* Group 1 — Communication */}
                                        <div className="mobile-menu-group mobile-menu-group--communication">
                                            <ul className="mobile-menu-list">
                                                <li>
                                                    <button
                                                        onClick={() => {
                                                            setShowMobileMenu(false);
                                                            setShowMobileNotifications(true);
                                                        }}
                                                        className="mobile-menu-link mobile-menu-link--button"
                                                    >
                                                        <FiBell className="mobile-menu-icon" />
                                                        <span>Уведомления</span>
                                                        {unreadCount > 0 && (
                                                            <span className="mobile-menu-badge">
                                                                {unreadCount > 99 ? '99+' : unreadCount}
                                                            </span>
                                                        )}
                                                    </button>
                                                </li>
                                                <li>
                                                    <Link
                                                        to="/chat"
                                                        onClick={() => setShowMobileMenu(false)}
                                                        className="mobile-menu-link"
                                                    >
                                                        <FiMessageSquare className="mobile-menu-icon" />
                                                        <span>Сообщения</span>
                                                    </Link>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="mobile-menu-divider" />

                                        {/* Group 2 — Action */}
                                        <div className="mobile-menu-group mobile-menu-group--action">
                                            <ul className="mobile-menu-list">
                                                <li>
                                                    <Link
                                                        to="/post-create"
                                                        onClick={() => setShowMobileMenu(false)}
                                                        className="mobile-menu-link mobile-menu-link--cta"
                                                    >
                                                        <FiPlusSquare className="mobile-menu-icon" />
                                                        <span>Новый пост</span>
                                                    </Link>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="mobile-menu-divider" />

                                        {/* Group 3 — Account */}
                                        <div className="mobile-menu-group mobile-menu-group--account">
                                            <ul className="mobile-menu-list">
                                                <li>
                                                    <Link
                                                        to="/profile"
                                                        onClick={() => setShowMobileMenu(false)}
                                                        className="mobile-menu-link"
                                                    >
                                                        <FiUser className="mobile-menu-icon" />
                                                        <span>Профиль</span>
                                                    </Link>
                                                </li>
                                                <li>
                                                    <button
                                                        onClick={handleLogout}
                                                        className="mobile-menu-link mobile-menu-link--button mobile-menu-link--logout"
                                                    >
                                                        <FiLogOut className="mobile-menu-icon" />
                                                        <span>Выйти</span>
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>

                                    </div>
                                )}
                                
                                {/* Currency Selector - visible for everyone */}
                                <CurrencySelectorMobile onClose={() => setShowMobileMenu(false)} />
                            </nav>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Mobile Notification Panel */}
            {isAuthenticated && (
                <NotificationPanelMobile 
                    isOpen={showMobileNotifications} 
                    onClose={() => setShowMobileNotifications(false)} 
                />
            )}
        </>
    );
};

export default Header;
